import React, { useCallback, useEffect, useMemo, useRef, useLayoutEffect, useState } from 'react';
import { createAddRowsComponent } from 'react-datasheet-grid';
import { useLiveQuery } from 'dexie-react-hooks';
import { NavLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Select from 'react-select';

import { useStore } from '../../store';
import { createLocalRecord, db, withExtras, withFormulas, withParents } from '../../Utils/Functions/Dexie-Setup';
import { submitDfrLocally } from '../../Utils/Functions/submission_helpers';


// UI Components
import { Loader, } from '../elements';
import {
  DataSheetInput,
  DateInput,
  MultipleChoiceInput,
  RelationshipInput,
  SRFButton,
  SignatureInput,
  TextInput,
  TextMultiLineInput,
} from '../Inputs';

const DeleteColumn = {
  component: ({ deleteRow }) => (
    <button type='button' className='text-center w-full h-full' onClick={deleteRow}>❌</button>
  ),
  basis: 50,
}

const EquipmentName = ({ focus, rowData, setRowData }) => {
  const equipmentOptions = ["Bucket Truck", "Auger Truck", "Excavator", "Trencher (Ditch Witch)", "Scissor Lift", "Boom Lift (JLG)"];
  const inputRef = useRef(null);

  useLayoutEffect(() => {
    if (focus) {
      inputRef.current?.focus()
    }
  }, [focus]);

  if (rowData.type === "Equipment") {
    return (
      <Select
        ref={inputRef}
        autoFocus={focus}
        value={rowData.name ? { value: rowData.name, label: rowData.name } : null}
        onChange={(e) => setRowData({ ...rowData, name: e.value })}
        options={equipmentOptions.map(opt => ({ value: opt, label: opt }))}
        menuPortalTarget={document.body}
        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
        className='w-full border-none'
      />
    )
  }

  return (
    <input
      ref={inputRef}
      type="text"
      className='w-full border-none'
      value={rowData.name || ''}
      onChange={(e) => setRowData({ ...rowData, name: e.target.value })}
    />
  );
}

function Form() {
  const { isAuthed, translation } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const navigate = useNavigate();
  const createdRid = useRef(false);
  const prevJobNumberRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const AddRows = useMemo(() => createAddRowsComponent({
    button: translation.add,
    unit: translation.rows,
  }), [translation]);

  const key = useMemo(() => Number(params?.key) || Number(atob(params?.key || "") ?? 0), [params]);

  const [currentSubmission, loaded] = useLiveQuery(() =>
    db.dailyFieldReports.get(key)
      .then(record => record ? withExtras("dailyFieldReports", [withParents, withFormulas])([record]) : undefined)
      .then(res => res ? res[0] : undefined)
      .then(res => [res, true]), [key], [undefined, false]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Prevent double-clicks

    // Un-submitting is a simple state change, not a full transactional submission.
    if (currentSubmission?.submitted) {
      await db.dailyFieldReports.update(key, { submitted: false, locallyUpdatedAt: Date.now() });
      return;
    }

    setIsSubmitting(true);
    try {
      const success = await submitDfrLocally(key);
      if (success) {
        navigate("/"); // Redirect on success
      } else {
        alert("There was an error submitting the ticket. Please try again.");
      }
    } finally {
      setIsSubmitting(false); // Re-enable button
    }
  };

  const laborFilter = useCallback((laborItem) => laborItem.relatedDailyFieldReport === key, [key]);
  const laborDefault = useMemo(() => ({
    relatedDailyFieldReport: key,
    position: "Foreman",
    shift: "First",
    hours: 4,
    earnCode: "Regular"
  }), [key]);

  const laborColumns = useMemo(() => [
    ["position", { title: translation.position, disabled: key > 0 }],
    ["earnCode", { title: translation.earnCode, disabled: key > 0, isCellEmpty: () => true }],
    ["shift", { title: "Shift", disabled: key > 0, isCellEmpty: () => true }],
    ["hours", { title: translation.hours, disabled: key > 0, isCellEmpty: () => true }],
    ["driver", { title: translation.driver, disabled: key > 0, isCellEmpty: () => true }],
  ], [translation, key]);

  const equipmentFilter = useCallback((equipmentItem) => equipmentItem.relatedDailyFieldReport === key, [key]);
  const equipmentDefault = useMemo(() => ({ relatedDailyFieldReport: key, quantity: 1 }), [key]);

  const equipmentColumns = useMemo(() => [
    ["type", { title: "Type", disabled: key > 0 }],
    ["name", { title: "Name", component: EquipmentName }],
    ["note", { title: "Note", disabled: key > 0 }],
    ["quantity", { title: "Quantity", disabled: key > 0 }],
  ], [key]);

  useEffect(() => {
    if (!isAuthed) {
      navigate("/auth");
    }
  }, [isAuthed, navigate]);

  useEffect(() => {
    if ((key && loaded) && !currentSubmission) {
      const timeout = setTimeout(() => navigate("/", { replace: true }), 4000);
      return () => clearTimeout(timeout);
    }
  }, [key, loaded, currentSubmission, navigate]);

  useEffect(() => {
    if (!key && !createdRid.current) {
      const defaultValues = {
        date: new Date().toISOString().split('T')[0],
        status: "Submitted"
      };
      createdRid.current = true;
      searchParams.forEach((value, key) => defaultValues[key] = value);
      (async () => {
        const newKey = await createLocalRecord("dailyFieldReports", defaultValues);
        navigate(`/form/${btoa(newKey)}`, { replace: true });
      })();
    }
  }, [key, navigate, searchParams]);

  useEffect(() => {
    const jobRecord = currentSubmission?.jobNumber_record;
    const currentJobNumber = currentSubmission?.jobNumber;

    if (jobRecord && jobRecord.location && currentJobNumber !== prevJobNumberRef.current) {
      const addressUpdate = {
        street1: jobRecord.street1 || null,
        street2: jobRecord.street2 || null,
        city: jobRecord.city || null,
        state: jobRecord.state || "Pennsylvania",
        postalCode: jobRecord.postalCode || null,
      };
      db.dailyFieldReports.update(key, addressUpdate);
    }
    prevJobNumberRef.current = currentJobNumber;
  }, [currentSubmission, key]);

  if (currentSubmission) {
    return (
      <form className='flex flex-col items-center py-4 gap-6' onSubmit={handleSubmit}>
        <div className='w-full flex justify-end lg:w-3/4 md:w-full px-4'>
          <NavLink to={`/form/${btoa(key)}/pdf`} rel="noopener noreferrer" style={{ backgroundColor: '#2563eb', color: 'white', padding: '12px 24px', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', textDecoration: 'none' }}>
            View PDF
          </NavLink>
        </div>
        <div className='flex flex-row flex-wrap justify-center lg:w-3/4 md:w-full'>
          <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
            <DateInput
              required
              disabled={key > 0}
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"date"}
              label={translation.date}
            />
          </div>
          <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
            <RelationshipInput
              required
              disabled={key > 0}
              tableName={"jobs"}
              fieldValue={currentSubmission?.jobNumber ?? null}
              setFieldValue={(jobNumber) => db.dailyFieldReports.update(key, { jobNumber: jobNumber })}
              fieldName={"jobNumber"}
              label={translation.job}
              displayFields={["jobNumber", "jobName"]}
            />
          </div>
          <div className='lg:w-1/3 md:w-1/2 w-full px-4'>
            <TextInput
              disabled={key > 0}
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"workOrderNumber"}
              label={translation.workOrderNum}
            />
          </div>
          <div className='w-full px-4'>
            <TextMultiLineInput
              disabled={key > 0}
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"notes"}
              label={translation.notes}
            />
          </div>
          <div className='w-full px-4'>
            <TextMultiLineInput
              disabled={key > 0}
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"followUp"}
              label={translation.followUp}
            />
          </div>
        </div>
        <h3 className='text-lg'>{translation.location}</h3>
        <div className='flex flex-row flex-wrap justify-center lg:w-3/4 md:w-full'>
          <div className='w-full px-4'>
            <TextInput
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"street1"}
              label={translation.street1}
            />
          </div>
          <div className='w-full px-4'>
            <TextInput
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"street2"}
              label={translation.street2}
            />
          </div>
          <div className='w-full px-4'>
            <TextInput
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"city"}
              label={translation.city}
            />
          </div>
          <div className='w-full px-4'>
            <MultipleChoiceInput
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"state"}
              label={translation.state}
            />
          </div>
          <div className='w-full px-4'>
            <TextInput
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"postalCode"}
              label={translation.postalCode}
            />
          </div>
        </div>
        <h3 className='text-lg'>{translation.laborAndEquipment}</h3>
        <div className='lg:w-3/4 w-full px-4'>
          <DataSheetInput
            tableName={"laborLineItems"}
            filter={laborFilter}
            defaultValues={laborDefault}
            columns={laborColumns}
            label={translation.laborLineItems}
            stickyRightColumn={key > 0 ? undefined : DeleteColumn}
            lockRows={key > 0}
            addRowsComponent={AddRows}
          />
        </div>
        <div className='lg:w-3/4 w-full px-4'>
          <DataSheetInput
            tableName={"equipmentLineItems"}
            filter={equipmentFilter}
            defaultValues={equipmentDefault}
            columns={equipmentColumns}
            label={"Equipment and Materials"}
            stickyRightColumn={key > 0 ? undefined : DeleteColumn}
            lockRows={key > 0}
            addRowsComponent={AddRows}
          />
        </div>
        <h3 className='text-lg'>{translation.signOff}</h3>
        <div className='flex flex-row flex-wrap justify-center lg:w-3/4 md:w-full gap-4'>
          <div className='w-full px-4'>
            <TextInput
              disabled={key > 0}
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"foremanName"}
              label={translation.foremanName}
            />
          </div>
          <div className='w-full px-4'>
            <SignatureInput
              disabled={key > 0}
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"foremanSignature"}
              label={translation.foremanSignature}
              buttonLabels={translation}
            />
          </div>
          <div className='w-full px-4'>
            <TextInput
              disabled={key > 0}
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"inspectorName"}
              label={translation.inspectorName}
            />
          </div>
          <div className='w-full px-4'>
            <SignatureInput
              disabled={key > 0}
              tableName={"dailyFieldReports"}
              primaryKey={key}
              fieldName={"inspectorSignature"}
              label={translation.inspectorSignature}
              buttonLabels={translation}
            />
          </div>
        </div>
        {
          key < 0 ?
            <div className='lg:w-3/3 md:w-1/2 w-full px-4'>
              <SRFButton type={"submit"} color='#0f314d' disabled={isSubmitting}>
                {isSubmitting ? translation.submitting : (currentSubmission?.submitted ? translation.unsubmit : translation.submit)}
              </SRFButton>
            </div>
            : null
        }
      </form>
    )
  } else {
    return <Loader />
  }

}

export default Form;
